import React, { useState, useEffect } from 'react';
import DecisionCard from './DecisionCard';
import LiveSummaryPanel from './LiveSummaryPanel';
import ProfileQuestion from './ProfileQuestion';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { ArrowRight, Sparkles, MapPin } from 'lucide-react';
import { profileQuestions } from '../../data/adventureData';

const DecisionEngine = ({ journey, onComplete }) => {
  const [currentDecisionIndex, setCurrentDecisionIndex] = useState(0);
  const [decisions, setDecisions] = useState({});
  const [totalDays, setTotalDays] = useState(journey.baseDuration);
  const [totalCost, setTotalCost] = useState(journey.basePrice);
  const [difficulty, setDifficulty] = useState(journey.baseDifficulty === 'easy-moderate' ? 1.5 : journey.baseDifficulty === 'moderate' ? 2 : 3);
  const [choicesSummary, setChoicesSummary] = useState([]);
  const [profileAnswers, setProfileAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [progress, setProgress] = useState(0);
  const [customItinerary, setCustomItinerary] = useState([]);

  useEffect(() => {
    // Initialize with default selected options
    const initialDecisions = {};
    journey.decisionPoints.forEach(point => {
      const selectedOption = point.options.find(opt => opt.selected);
      if (selectedOption) {
        initialDecisions[point.id] = selectedOption.id;
      }
    });
    setDecisions(initialDecisions);
  }, [journey]);

  useEffect(() => {
    // Update progress based on decisions made
    const totalDecisions = journey.decisionPoints.length;
    const madeDecisions = Object.keys(decisions).length;
    setProgress((madeDecisions / totalDecisions) * 100);
  }, [decisions, journey.decisionPoints.length]);

  const handleDecisionSelect = (decisionId, optionId) => {
    const newDecisions = { ...decisions, [decisionId]: optionId };
    setDecisions(newDecisions);

    // Recalculate totals and build custom itinerary
    let days = journey.baseDuration;
    let cost = journey.basePrice;
    let diff = journey.baseDifficulty === 'easy-moderate' ? 1.5 : journey.baseDifficulty === 'moderate' ? 2 : 3;
    const choices = [];
    const itinerary = [...journey.baseItinerary];

    journey.decisionPoints.forEach(point => {
      const selectedOptionId = newDecisions[point.id];
      const selectedOption = point.options.find(opt => opt.id === selectedOptionId);
      
      if (selectedOption && !selectedOption.selected) {
        days += selectedOption.impact.days;
        cost += selectedOption.impact.cost;
        diff += selectedOption.impact.difficulty;
        choices.push(selectedOption.title);
        
        // Add custom itinerary items
        if (selectedOption.addedItinerary && selectedOption.addedItinerary.length > 0) {
          selectedOption.addedItinerary.forEach(item => {
            itinerary.push({
              day: itinerary.length + 1,
              title: item.title,
              isCustom: true
            });
          });
        }
      }
    });

    setTotalDays(days);
    setTotalCost(cost);
    setDifficulty(diff);
    setChoicesSummary(choices);
    setCustomItinerary(itinerary);

    // Show profile question based on timing
    const decisionCount = Object.keys(newDecisions).length;
    if (decisionCount === 1) {
      showQuestionByTiming('afterFirstChoice');
    } else if (decisionCount === 2) {
      showQuestionByTiming('afterSecondChoice');
    } else if (decisionCount === 3) {
      showQuestionByTiming('afterThirdChoice');
    } else if (decisionCount === 4) {
      showQuestionByTiming('afterFourthChoice');
    }
  };

  const showQuestionByTiming = (timing) => {
    const question = profileQuestions.find(q => q.timing === timing && !answeredQuestions.includes(q.id));
    if (question) {
      setTimeout(() => setCurrentQuestion(question), 500);
    }
  };

  const handleProfileAnswer = (questionId, answer) => {
    setProfileAnswers({ ...profileAnswers, [questionId]: answer });
    setAnsweredQuestions([...answeredQuestions, questionId]);
    setCurrentQuestion(null);
  };

  const handleNext = () => {
    if (currentDecisionIndex < journey.decisionPoints.length - 1) {
      setCurrentDecisionIndex(currentDecisionIndex + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Show final question before completing
      const finalQuestion = profileQuestions.find(q => q.timing === 'beforeFinal' && !answeredQuestions.includes(q.id));
      if (finalQuestion && !answeredQuestions.includes(finalQuestion.id)) {
        setCurrentQuestion(finalQuestion);
        setTimeout(() => {
          onComplete({
            journey,
            decisions,
            totalDays,
            totalCost,
            difficulty,
            choicesSummary,
            profileAnswers: { ...profileAnswers }
          });
        }, 3000);
      } else {
        onComplete({
          journey,
          decisions,
          totalDays,
          totalCost,
          difficulty,
          choicesSummary,
          profileAnswers,
          customItinerary: customItinerary.length > 0 ? customItinerary : journey.baseItinerary
        });
      }
    }
  };

  const currentDecisionPoint = journey.decisionPoints[currentDecisionIndex];

  return (
    <div className="min-h-screen bg-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-serif mb-4 text-zinc-900">
            {journey.name}
          </h1>
          <p className="text-lg text-zinc-600 mb-2">Shape Your Adventure</p>
          <div className="flex items-center justify-center gap-2 text-sm text-zinc-500">
            <span>Decision {currentDecisionIndex + 1} of {journey.decisionPoints.length}</span>
            <Sparkles className="w-4 h-4 text-amber-500" />
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-8">
            {/* Profile Question (if active) */}
            {currentQuestion && (
              <ProfileQuestion
                question={currentQuestion}
                onAnswer={handleProfileAnswer}
                isAnswered={answeredQuestions.includes(currentQuestion.id)}
              />
            )}

            {/* Decision Point */}
            <div>
              <div className="mb-8">
                <h2 className="text-3xl font-serif mb-3 text-zinc-900">
                  {currentDecisionPoint.title}
                </h2>
                <p className="text-lg text-zinc-600">
                  {currentDecisionPoint.description}
                </p>
              </div>

              {/* Options */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {currentDecisionPoint.options.map(option => (
                  <DecisionCard
                    key={option.id}
                    option={option}
                    isSelected={decisions[currentDecisionPoint.id] === option.id}
                    onSelect={handleDecisionSelect}
                    decisionId={currentDecisionPoint.id}
                  />
                ))}
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center">
                <Button
                  variant="outline"
                  onClick={() => setCurrentDecisionIndex(Math.max(0, currentDecisionIndex - 1))}
                  disabled={currentDecisionIndex === 0}
                  className="border-2 border-zinc-300 rounded-sm"
                >
                  Previous
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={!decisions[currentDecisionPoint.id]}
                  className="bg-emerald-800 hover:bg-emerald-900 text-white px-8 py-6 rounded-sm"
                >
                  {currentDecisionIndex === journey.decisionPoints.length - 1 ? 'Complete Journey' : 'Next Decision'}
                  <ArrowRight className="ml-2" />
                </Button>
              </div>
            </div>

            {/* Live Itinerary */}
            <Card className="border-2 border-zinc-200 rounded-sm">
              <CardContent className="p-6">
                <div className="mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-emerald-700" />
                  <h3 className="text-xl font-serif text-zinc-900">Your Itinerary</h3>
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {(customItinerary.length > 0 ? customItinerary : journey.baseItinerary).map((item, idx) => (
                    <div key={idx} className={`flex items-start gap-3 p-3 rounded-sm ${item.isCustom ? 'bg-amber-50 border border-amber-200' : 'bg-zinc-50'}`}>
                      <div className={`w-8 h-8 rounded-full text-white flex items-center justify-center text-sm font-semibold flex-shrink-0 ${item.isCustom ? 'bg-amber-600' : 'bg-emerald-700'}`}>
                        {item.day}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-zinc-900">{item.title}</p>
                        {item.isCustom && (
                          <span className="text-xs text-amber-600 font-semibold">Custom Addition</span>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="mt-4 p-3 bg-emerald-50 rounded-sm border border-emerald-200">
                    <p className="text-sm text-emerald-900 text-center">
                      <span className="font-semibold">{totalDays} days</span> journey · <span className="font-semibold">${totalCost}</span> estimated cost
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Live Summary */}
          <div className="lg:col-span-1">
            <LiveSummaryPanel
              journey={journey}
              totalDays={totalDays}
              totalCost={totalCost}
              difficulty={difficulty}
              choices={choicesSummary}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecisionEngine;